<script lang="ts">
  import { tick } from "svelte";
  import { buildSwimCalendar, type CalendarDay } from "../lib/swimCalendar";
  import CalendarMonth from "./CalendarMonth.svelte";
  import DayPicker from "./DayPicker.svelte";
  import { workouts } from "../lib/workoutsStore";
  import { selectedWorkoutUUID } from "../lib/state";
  import { closeOverlay } from "../lib/overlay";
  import { haptic } from "../lib/haptics";
  import { lockBodyScroll, unlockBodyScroll } from "../lib/native/bodyScrollLock";
  import { sheetDrag } from "../lib/sheetDrag";
  import { groupSessions, type MemberSwim } from "../lib/clubSessions";
  import { buildClubCalendar, clubDayPicked, formatClubMonthSummary, sessionForDay } from "../lib/clubCalendar";
  import { selectedSessionId } from "../lib/state";

  interface Props {
    open?: boolean;
    /** Every club member's swims: set = the Club variant (a swimmer count per
     *  day; a tap opens that day on the Club tab). Unset = "Your swims". */
    club?: MemberSwim[] | null;
  }
  let { open = false, club = null }: Props = $props();

  let months = $derived(club ? buildClubCalendar(club) : buildSwimCalendar($workouts));
  let noSwimsEver = $derived(club ? club.length === 0 : $workouts.length === 0);
  let pickerDay = $state<CalendarDay | null>(null);
  let scroller = $state<HTMLDivElement | null>(null);
  let selectedDayKey = $state<string | null>(null);

  const dayKey = (d: CalendarDay) =>
    `${d.date.getFullYear()}-${d.date.getMonth()}-${d.date.getDate()}`;

  function close() {
    closeOverlay("your-swims");
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") {
      if (pickerDay) pickerDay = null;
      else close();
    }
  }

  function pick(uuid: string) {
    haptic("selection");
    selectedWorkoutUUID.set(uuid);
    pickerDay = null;
    selectedDayKey = null;
    close();
  }

  // Club variant: the day's session becomes the Club tab's shown day.
  function pickClubDay(day: CalendarDay) {
    const session = sessionForDay(groupSessions(club ?? []), day.date);
    if (!session) return;
    haptic("selection");
    selectedSessionId.set(session.sessionId);
    clubDayPicked.update((n) => n + 1);
    close();
  }

  async function onSelectDay(day: CalendarDay) {
    if (club) {
      pickClubDay(day);
    } else if (day.swims.length === 1) {
      pick(day.swims[0].uuid);
    } else if (day.swims.length > 1) {
      selectedDayKey = dayKey(day);
      pickerDay = day;
      // Wait for the picker + 60vh scroll padding to render so there's room to lift the tapped day above the sheet.
      await tick();
      const cell = scroller?.querySelector(`[data-day-key="${selectedDayKey}"]`);
      if (cell && scroller) {
        const top = cell.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
        scroller.scrollTop = Math.max(0, top - 90); // bring the tapped day near the top, above the sheet
      }
    }
  }

  // Lock scroll while open; reset the picker; scroll to the current (last) month.
  $effect(() => {
    if (!open) {
      pickerDay = null;
      selectedDayKey = null;
      return;
    }
    lockBodyScroll();
    // Current month is last (oldest first); scroll to it only if it would otherwise start below the fold.
    if (scroller) {
      const monthEls = scroller.querySelectorAll("[data-month]");
      const cur = monthEls[monthEls.length - 1];
      if (cur) {
        const top = cur.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
        // Few months -> current month already above the fold -> stay at top (no first-row tuck).
        scroller.scrollTop = top >= scroller.clientHeight ? Math.max(0, top - 8) : 0;
      } else {
        scroller.scrollTop = scroller.scrollHeight;
      }
    }
    return () => unlockBodyScroll();
  });
</script>

<svelte:window onkeydown={onKey} />

{#if open}
  <div
    class="scrim"
    role="presentation"
    onclick={close}
    onkeydown={(e) => e.key === "Enter" && close()}
  ></div>

  <div class="sheet" role="dialog" aria-modal="true" aria-label={club ? "Club swims" : "Your swims"} use:sheetDrag={close}>
    <header class="sheet-head">
      <!-- The grabber every other sheet shows: the cue that a drag down closes it. -->
      <div class="grabber" aria-hidden="true"></div>
      <h2 class="sheet-title">{club ? "Club swims" : "Your swims"}</h2>
      {#if !pickerDay}
        <button type="button" class="sheet-close" aria-label="Close" onclick={close}>Done</button>
      {/if}
    </header>

    <div class="scroller" class:picker-room={pickerDay} bind:this={scroller}>
      {#if noSwimsEver}
        <p class="empty-note">{club ? "No club swims yet." : "No swims yet."}</p>
      {/if}
      {#each months as month (month.label)}
        {#if club}
          <CalendarMonth {month} {onSelectDay} {selectedDayKey} marker="count" summary={formatClubMonthSummary} />
        {:else}
          <CalendarMonth {month} {onSelectDay} {selectedDayKey} />
        {/if}
      {/each}
    </div>
  </div>

  {#if pickerDay}
    <DayPicker day={pickerDay} onPick={pick} onClose={() => { pickerDay = null; selectedDayKey = null; }} />
  {/if}
{/if}

<style>
  .scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 50;
    animation: scrim-in var(--dur-card) var(--ease-swim);
    touch-action: none;
  }
  .sheet {
    position: fixed;
    inset: 0;
    z-index: 51;
    background: var(--bg-canvas);
    display: flex;
    flex-direction: column;
    animation: sheet-in var(--dur-card) var(--ease-swim);
  }
  .sheet-head {
    position: relative;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: calc(env(safe-area-inset-top) + var(--space-4)) var(--space-4) var(--space-3);
  }
  /* The other sheets' grabber, top centre just below the status bar (SetDetailSheet's). */
  .grabber {
    position: absolute;
    top: calc(env(safe-area-inset-top, 0px) + 8px);
    left: 50%;
    width: 36px;
    height: 4px;
    margin-left: -18px;
    border-radius: 999px;
    background: var(--divider-strong);
  }
  .sheet-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: 22px;
    font-weight: 700;
    color: var(--text-primary);
  }
  .sheet-close {
    min-width: 44px;
    min-height: 44px;
    border: 0;
    background: transparent;
    color: var(--accent-primary);
    font-size: 17px;
    font-family: var(--font-text);
    cursor: pointer;
  }
  .scroller {
    flex: 1 1 auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y; /* NEVER touch-action: none (WKWebView trap) */
    overscroll-behavior: contain;
    padding: 0 var(--space-4) calc(env(safe-area-inset-bottom) + var(--space-6));
    display: flex;
    flex-direction: column;
    gap: var(--space-6);
  }
  .scroller.picker-room {
    padding-bottom: 60vh;
  }
  .empty-note {
    margin: var(--space-4) 0 0;
    font-size: 16px;
    color: var(--text-muted);
    text-align: center;
  }
  @keyframes scrim-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes sheet-in {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
</style>
